import React, { useState } from 'react';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';
import { apiDeleteRequest } from 'src/utils/mlPigeonAPIRequest';
import { urlList } from 'src/context/url';

export const ModelArtifactModal = ({
  values,
  onClose,
  visible,
}: {
  values?: {
    modelVersionId: string;
    modelArtifactId: string;
  } | null;
  onClose: () => void;
  visible: boolean;
}) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);

  const [error, setError] = useState<Error | null>(null);

  const deleteModelArtifact = (
    {
      modelArtifactId,
      modelVersionId,
    }: { modelArtifactId: string; modelVersionId: string },
    queryClient: QueryClient,
  ) => {
    return apiDeleteRequest({
      endpoint: `${urlList.modelArtifacts}/${modelArtifactId}`,
    }).then(() => {
      queryClient.invalidateQueries({
        queryKey: ['modelArtifacts', modelVersionId],
      });
    });
  };

  if (!values || !visible) return <></>;

  const handleDeleteConfirm = async () => {
    setLoading(true);
    const response = await deleteModelArtifact(
      {
        modelArtifactId: values.modelArtifactId,
        modelVersionId: values.modelVersionId,
      },
      queryClient,
    )
      .catch((e) => {
        setError(e);
        return e.message;
      })
      .finally(() => {
        setLoading(false);
      });

    if (!response) onClose();
  };

  return (
    <Modal
      visible={true}
      onDismiss={onClose}
      header={'Delete Model Artifact'}
      footer={
        <Box float="right">
          <SpaceBetween size="m" direction="horizontal">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <PermissionAwareButton
              requiredPermission="ADMINISTER"
              actionDescription={'delete model artifacts'}
              loading={loading}
              variant={'primary'}
              onClick={() => {
                handleDeleteConfirm();
              }}
            >
              Delete Model Artifact
            </PermissionAwareButton>
          </SpaceBetween>
        </Box>
      }
    >
      <p>
        This will delete the Model Artifact and all the operator flow jobs
        within it. The Sagemaker jobs and endpoints that were created in your
        AWS account will not be deleted. Confirm?
      </p>
      {error && <p className="error-text">{error.message}</p>}
    </Modal>
  );
};
